// @flow
import { AdSlot, GraphController, Grid, GridItem, MobileGraphController, Query, SeoData, StaticTable, useMedia, } from '@haaretz/htz-components';
import React from 'react';
import { FelaComponent, FelaTheme, } from 'react-fela';

import type { Node, } from 'react';

import AdditionalInfo from '../../components/AdditionalInfo/AdditionalInfo';
import AssetStaticGraphs from '../../components/AssetStaticGraphs/AssetStaticGraphs';
import PageRow from '../../components/PageRow/PageRow';
import QuoteInfoTable from '../../components/QuotePageComponents/QuoteInfoTable/QuoteInfoTable';
import QuoteSummary from '../../components/QuotePageComponents/QuoteSummary/QuoteSummary';
import RelatedAssets from '../../components/QuotePageComponents/RelatedAssets/RelatedAssets';
import ShareHolders from '../../components/QuotePageComponents/ShareHolders/ShareHolders';
import RowItem from '../../components/RowItem/RowItem';
import MainLayout from '../../layouts/MainLayout';
import BondQuery from '../queries/bondData.graphql';
import useAllAdUnits from '../../components/AdManager/AdUnitsProvider';
import useAdUnitPathTemplate, { createAdSlotProps, } from '../../components/AdManager/useAdUnitPathTemplate';

type Props = {
  url: {
    pathname: string,
    query: {
      assetId: string,
      section: string,
    },
    asPath: string,
  },
};

const numToString: number => string = num => num.toLocaleString('he', { minimumFractionDigits: 2, maximumFractionDigits: 2, });

function bond({
  url: {
    query: { assetId, section, },
    asPath,
  },
}: Props): Node {
  const isMobile = useMedia({ query: { from: 's', }, });
  const adUnits = useAllAdUnits();
  const adUnitPathTemplate = useAdUnitPathTemplate();

  return (
    <MainLayout section={section}>
      <Query query={BondQuery} variables={{ assetId: [ assetId, ], }}>
        {({ loading, error, data, }) => {
          if (error) return null;
          if (loading) return null;
          if (!data.assets?.length) return null;

          const {
            name,
            value,
            changePercentage,
            numeralChange,
            subType,
            assetNumber,
            assetSummary,
            lastTradeTime,
            relatedAssets,
            shareHolders,
            amrtTable,
            ...staticGraphsData
          } = data.assets[0];
          return (
            <FelaTheme>
              {theme => (
                <FelaComponent
                  style={{
                    extend: [
                      theme.mq(
                        { until: 'xl', },
                        {
                          display: 'flex',
                          flexDirection: 'column',
                        }
                      ),
                    ],
                  }}
                >
                  <SeoData
                    title={`${name} -אג"ח - אגרות חוב - TheMarker Finance`}
                    description={`${name} - למידע עדכני על נתוני אג"ח - אגרות חוב, היכנסו לאתר TheMarker Finance`}
                    path={asPath}
                  />
                  <PageRow lines={2} miscStyles={{ position: 'relative', order: '1', }}>
                    <RowItem
                      title={name}
                      miscStyles={{
                        ...theme.type(5),
                      }}
                    />
                    {
                      assetSummary
                        ? (
                          <AdditionalInfo
                            title='למידע נוסף על האג"ח'
                            info={assetSummary}
                            miscStyles={{
                              position: 'absolute',
                              left: '0',
                              bottom: '1rem',
                              zIndex: '2',
                              width: '41rem',
                            }}
                          />
                        )
                        : null
                    }
                  </PageRow>
                  <PageRow miscStyles={{ order: '2', }}>
                    <QuoteSummary
                      valueData={[
                        { title: 'שער', value: value ? value.toString() : '0', },
                        { title: '% שינוי', value: changePercentage, },
                        { title: 'שינוי באג׳', value: numeralChange, },
                      ]}
                      date={{ title: 'נכון ל:', value: lastTradeTime, }}
                      assetInfo={[
                        { title: 'סוג נייר:', value: subType, },
                        { title: 'מספר נייר:', value: assetNumber, },
                      ]}
                    />
                  </PageRow>
                  <PageRow
                    miscStyles={{
                      marginBottom: '2rem',
                      order: '3',
                    }}
                  >
                    <RelatedAssets assets={relatedAssets} />
                  </PageRow>
                  <PageRow miscStyles={{ order: '4', }}>
                    {
                      isMobile
                        ? <GraphController assetId={assetId} width={900} />
                        : <MobileGraphController assetId={assetId} />
                    }
                  </PageRow>
                  <PageRow miscStyles={{ order: '5', }}>
                    <Grid
                      gutter={2}
                      miscStyles={{
                        paddingStart: '0rem',
                        paddingEnd: '0rem',
                      }}
                    >
                      <QuoteInfoTable
                        id={assetId}
                        miscStyles={{ marginBottom: [ { until: 's', value: '4rem', }, ], }}
                        width={[
                          { until: 'l', value: 1, },
                          { from: 'l', value: 1 / 3, },
                        ]}
                        title="נתוני המסחר"
                        tradingStatus
                        fixed
                        fields={[
                          { name: 'baseValue', display: 'שער בסיס', },
                          { name: 'openingValue', display: 'שער פתיחה', },
                          { name: 'value', display: 'שער אחרון', },
                          { name: 'dailyLow', display: 'נמוך יומי', },
                          { name: 'dailyHigh', display: 'גבוה יומי', },
                          { name: 'standardDeviation', display: 'סטיית תקן', },
                        ]}
                      />
                      <GridItem
                        width={[
                          { until: 'l', value: 1, },
                          { from: 'l', value: 2 / 3, },
                        ]}
                      >
                        <AssetStaticGraphs {...staticGraphsData} />
                      </GridItem>
                    </Grid>
                  </PageRow>
                  <PageRow
                    miscStyles={{
                      order: [
                        { until: 's', value: '7', },
                        { until: 'l', value: '6', },
                        { from: 'l', value: '7', },
                        { from: 'xl', value: '6', },
                      ],
                    }}
                  >
                    <RowItem title="חישובי אג״ח" miscStyles={{ marginBottom: '4rem', }}>
                      <Grid
                        gutter={2}
                        miscStyles={{
                          paddingStart: '0rem',
                          paddingEnd: '0rem',
                        }}
                      >
                        <QuoteInfoTable
                          id={assetId}
                          width={[
                            { until: 'l', value: 1, },
                            { from: 'l', value: 1 / 3, },
                          ]}
                          fields={[
                            { name: 'yieldToMaturity', display: 'תשואה לפדיון', },
                            { name: 'per', display: 'פארי', },
                            { name: 'avgDuration', display: 'מח"מ', },
                            { name: 'yieldFactor', display: 'מקדם תשואה', },
                            { name: 'daysToMaturity', display: 'זמן סופי לפדיון', },
                          ]}
                        />
                        <QuoteInfoTable
                          id={assetId}
                          width={[
                            { until: 'l', value: 1, },
                            { from: 'l', value: 1 / 3, },
                          ]}
                          fields={[
                            { name: 'linkageType', display: 'סיווג', },
                            { name: 'issueDate', display: 'תאריך הנפקה', type: 'date', },
                            { name: 'redemptionDate', display: 'תאריך פדיון', type: 'date', },
                            { name: 'periodicalInterest', display: 'ריבית תקופתית', },
                            { name: 'yearlyInterest', display: 'ריבית שנתית', },
                          ]}
                        />
                        <QuoteInfoTable
                          id={assetId}
                          width={[
                            { until: 'l', value: 1, },
                            { from: 'l', value: 1 / 3, },
                          ]}
                          fields={[
                            { name: 'retailTax', display: 'מס ליחידים', },
                            { name: 'linkageType', display: 'סוג הצמדה', },
                            { name: 'paymentDate', display: 'תאריך תשלום', type: 'date', },
                          ]}
                        />
                      </Grid>
                    </RowItem>
                  </PageRow>
                  <PageRow
                    miscStyles={{
                      order: [
                        { until: 's', value: '6', },
                        { until: 'l', value: '10', },
                        { from: 'l', value: '6', },
                        { from: 'xl', value: '7', },
                      ],
                    }}
                  >
                    <RowItem>
                      <AdSlot {...createAdSlotProps(adUnits['finance.themarker.com.banner1'], adUnitPathTemplate)} />
                    </RowItem>
                  </PageRow>
                  <PageRow miscStyles={{ order: '8', }}>
                    <Grid
                      gutter={2}
                      miscStyles={{
                        paddingStart: '0rem',
                        paddingEnd: '0rem',
                      }}
                    >
                      <GridItem
                        width={[
                          { until: 'l', value: 1, },
                          { from: 'l', value: 65 / 100, },
                        ]}
                      >
                        <RowItem title="תחזית אירועים">
                          <StaticTable
                            data={amrtTable}
                            columns={[
                              {
                                name: 'DateEX',
                                title: 'תאריך אקס',
                                styles: {
                                  paddingStart: '2rem',
                                  fontWeight: '700',
                                  width: '50%',
                                },
                                render: value => new Date(value).toLocaleString('it-It', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                }),
                              },
                              {
                                name: 'DatePay',
                                title: 'תאריך תשלום',
                                styles: {
                                  paddingStart: '2rem',
                                  direction: 'ltr',
                                  textAlign: 'start',
                                },
                                render: value => new Date(value).toLocaleString('it-It', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                }),
                              },
                              {
                                name: 'PayPercent',
                                title: '% פדיון',
                                styles: {
                                  paddingStart: '2rem',
                                  direction: 'ltr',
                                  textAlign: 'start',
                                },
                                render: value => `${numToString(value || 0)}%`,
                              },
                              {
                                name: 'PayType',
                                title: 'סוג תשלום',
                                styles: {
                                  paddingStart: '2rem',
                                  direction: 'ltr',
                                  textAlign: 'start',
                                },
                                render: value => value,
                              },
                            ]}
                          />
                        </RowItem>
                      </GridItem>
                    </Grid>
                  </PageRow>
                  <PageRow miscStyles={{ order: '9', }}>
                    <Grid
                      gutter={2}
                      miscStyles={{
                        paddingStart: '0rem',
                        paddingEnd: '0rem',
                      }}
                    >
                      <QuoteInfoTable
                        id={assetId}
                        width={[
                          { until: 'l', value: 1, },
                          { from: 'l', value: 1 / 3, },
                        ]}
                        title="יחסים פיננסיים"
                        miscStyles={{ marginBottom: '2rem', }}
                        fields={[
                          { name: 'peRatio', display: 'מכפיל רווח', },
                          { name: 'pbRatio', display: 'מכפיל הון', },
                          { name: 'roe', display: 'תשואה על ההון העצמי', },
                          { name: 'psRatio', display: 'מכפיל מכירות', },
                        ]}
                      />
                      <GridItem
                        width={[
                          { until: 'l', value: 1, },
                          { from: 'l', value: 2 / 3, },
                        ]}
                      >
                        <RowItem title="בעלי עניין קונצרני">
                          <ShareHolders data={shareHolders} />
                        </RowItem>
                      </GridItem>
                    </Grid>
                  </PageRow>
                  <PageRow
                    miscStyles={{
                      order: '10',
                      display: [ { from: 's', until: 'l', value: 'none', }, ],
                    }}
                  >
                    <RowItem>
                      <AdSlot {...createAdSlotProps(adUnits['finance.themarker.com.banner2'], adUnitPathTemplate)} />
                    </RowItem>
                  </PageRow>
                </FelaComponent>
              )}
            </FelaTheme>
          );
        }}
      </Query>
    </MainLayout>
  );
}

export default bond;
